import React, { useEffect, useState } from 'react'

import i18n from 'i18next'
import { I18nextProvider } from 'react-i18next'

import { env } from '@/core/environments'

// Fallback translations in case the external service is unreachable
import enFallbackAuth from './fallback/en/auth.json'
import enFallbackCampaigns from './fallback/en/campaigns.json'
import enFallbackCommon from './fallback/en/common.json'
import enFallbackZones from './fallback/en/zones.json'
import esFallbackAuth from './fallback/es/auth.json'
import esFallbackCampaigns from './fallback/es/campaigns.json'
import esFallbackCommon from './fallback/es/common.json'
import esFallbackZones from './fallback/es/zones.json'

interface TranslationProviderProps {
	children: React.ReactNode
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({
	children,
}) => {
	const [isLoaded, setIsLoaded] = useState(false)

	useEffect(() => {
		/**
		 * Keep fallback resources merged in, even when remote bundles load.
		 *
		 * Remote translation loading can overwrite the in-memory bundle for a given
		 * language/namespace. By re-merging fallback bundles with overwrite=false,
		 * we ensure missing remote keys still resolve to local fallback strings.
		 */
		const ensureFallbackResources = () => {
			// deep=true, overwrite=false => only fill missing keys
			i18n.addResourceBundle('en', 'common', enFallbackCommon, true, false)
			i18n.addResourceBundle('en', 'auth', enFallbackAuth, true, false)
			i18n.addResourceBundle('en', 'zones', enFallbackZones, true, false)
			i18n.addResourceBundle(
				'en',
				'campaigns',
				enFallbackCampaigns,
				true,
				false
			)
			// Ensure English variants (e.g. en-US from Instagram WebView) resolve keys
			i18n.addResourceBundle('en-US', 'common', enFallbackCommon, true, false)
			i18n.addResourceBundle('en-US', 'auth', enFallbackAuth, true, false)
			i18n.addResourceBundle('en-US', 'zones', enFallbackZones, true, false)
			i18n.addResourceBundle(
				'en-US',
				'campaigns',
				enFallbackCampaigns,
				true,
				false
			)
			i18n.addResourceBundle('es', 'common', esFallbackCommon, true, false)
			i18n.addResourceBundle('es', 'auth', esFallbackAuth, true, false)
			i18n.addResourceBundle('es', 'zones', esFallbackZones, true, false)
			i18n.addResourceBundle(
				'es',
				'campaigns',
				esFallbackCampaigns,
				true,
				false
			)
			// Ensure Spanish variants resolve campaign keys (e.g. es-ES)
			i18n.addResourceBundle('es-ES', 'common', esFallbackCommon, true, false)
			i18n.addResourceBundle('es-ES', 'auth', esFallbackAuth, true, false)
			i18n.addResourceBundle('es-ES', 'zones', esFallbackZones, true, false)
			i18n.addResourceBundle(
				'es-ES',
				'campaigns',
				esFallbackCampaigns,
				true,
				false
			)
		}

		ensureFallbackResources()

		// Add event listeners for loading errors
		const handleError = (error: any) => {
			if (env.NODE_ENV === 'development') {
				console.warn('Translation loading error:', error)
			}
			// Translations will fallback to local bundles
		}

		i18n.on('failedLoading', handleError)
		i18n.on('loaded', ensureFallbackResources)
		i18n.on('languageChanged', ensureFallbackResources)

		// Mark as loaded
		setIsLoaded(true)

		return () => {
			i18n.off('failedLoading', handleError)
			i18n.off('loaded', ensureFallbackResources)
			i18n.off('languageChanged', ensureFallbackResources)
		}
	}, [])

	if (!isLoaded) {
		return <div>Initializing translations...</div>
	}

	return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}
